import { z } from "zod";
import { ExerciseElement } from "./exercises.ts";

/**
 * Тест на весь пройденный курс: контракт сборки.
 * Проект целиком в `docs/course-test-design.md`.
 *
 * Преподаватель на уроке выбирает класс, сервер собирает тест по всем темам,
 * пройденным до этого класса включительно, и возвращает его разложенным по
 * слайдам. Ученик решает при преподавателе, проверка автоматическая.
 *
 * ⚠️ **Собирается ОДНИМ запросом, а не тринадцатью вызовами
 * `POST /v1/lessons/:id/exercises`.** Причин три, и каждой хватило бы: у той
 * ручки стоит лимит частоты, и стоит он не случайно (она самая дорогая в
 * проекте); тринадцать вызовов не атомарны, а половина теста на доске хуже, чем
 * его отсутствие; и замок комнаты брался бы тринадцать раз подряд.
 */

/**
 * Сколько минут теста собираем.
 *
 * ⚠️ **Одно значение, и форма выбора под него не делается** (решение владельца
 * 04.09.2026). Константа заведена ровно затем, чтобы второе значение стоило
 * одной строки, а не поиска числа по коду.
 */
export const COURSE_TEST_MINUTES = 20;

/**
 * Классы, за которые можно собрать тест.
 *
 * ⚠️ **Строки, а не числа, и это не мелочь.** Класс темы в каталоге записан
 * диапазоном (`5-7`, `8-9`), и число здесь означало бы, что где-то рядом живёт
 * второе представление того же понятия. Разбор диапазонов один и лежит в правиле
 * отбора.
 *
 * Пятый класс в списке есть, хотя тем с генераторами у него сегодня две: решать,
 * что тест «слишком короткий», продукт не должен молча. Сколько тем нашлось,
 * говорит ответ, и это видит преподаватель.
 */
export const COURSE_TEST_GRADES = ["5", "6", "7", "8", "9"] as const;
export const CourseTestGrade = z.enum(COURSE_TEST_GRADES);
export type CourseTestGrade = z.infer<typeof CourseTestGrade>;

/**
 * Заказ теста.
 *
 * ⚠️ **`testId` это ключ идемпотентности, и он обязателен.** Сборка зовёт
 * генераторы и пишет эталоны, то есть повтор потерянного запроса без ключа
 * положил бы на доску второй тест. Идентификаторы заданий выводятся из него
 * детерминированно, поэтому повтор попадает в уже записанные строки и возвращает
 * тот же тест, без второго вызова генераторов. Того же рода ключ и по той же
 * причине стоит у `AddLessonExerciseRequest.elementId`.
 */
export const CourseTestRequest = z.object({
  testId: z.string().min(8).max(64),
  grade: CourseTestGrade,
  /**
   * Бюджет времени. Необязателен: отсутствие это `COURSE_TEST_MINUTES`.
   *
   * Поле заведено сразу, потому что оно и есть та самая одна строка, которой
   * стоит второе значение. Форма его пока не показывает.
   */
  minutes: z.number().int().min(5).max(90).optional(),
});
export type CourseTestRequest = z.infer<typeof CourseTestRequest>;

/**
 * Слайд теста: сколько заданий влезло, столько и лежит.
 *
 * ⚠️ **Раскладку по слайдам считает СЕРВЕР, а не комната**, и по той же причине,
 * по которой он считает рамку одного задания (шапка `exercise-frame.ts`):
 * размер коробки зависит от вида задания и числа вариантов, а вид решает
 * генератор. Комната узнаёт его только из этого ответа, то есть посчитанная там
 * раскладка была бы раскладкой не по содержимому.
 *
 * ⚠️ Схемой пользуются ДВОЕ: тест на курс и пачка заданий на уроке
 * (`./lesson-batch.ts`), у которой смысл слайда тот же и раскладка та же.
 */
export const CourseTestSlide = z.object({
  exercises: z.array(ExerciseElement).min(1),
});
export type CourseTestSlide = z.infer<typeof CourseTestSlide>;

/**
 * Собранный тест.
 *
 * ⚠️ **Числа состава едут в ответе, а не считаются на клиенте.** Число заданий
 * клиент вывел бы и сам, а вот сколько РАЗНЫХ ТЕМ вошло, из показываемой
 * половины не следует вовсе: тема живёт за барьером вместе с эталонами. А
 * сказать преподавателю надо именно её: «13 заданий по 11 темам» это то, что он
 * оценивает на глаз мгновенно, в отличие от двадцати минут.
 *
 * ⚠️ **`already` отличает повтор от новой сборки**, ровно как у ручки одного
 * задания: без него потерянный ответ выглядел бы как второй тест.
 */
export const CourseTestResponse = z.object({
  slides: z.array(CourseTestSlide),
  /** Сколько разных тем вошло в тест. */
  topics: z.number().int().min(0),
  /** Сколько минут набрал состав по нашим весам. Не таймер и не обещание. */
  minutes: z.number().int().min(0),
  already: z.boolean().default(false),
});
export type CourseTestResponse = z.infer<typeof CourseTestResponse>;
